// @flow strict
/* eslint-disable ta-code-conventions/has-matching-storydoc,ta-code-conventions/has-matching-test */
import * as React from 'react';
import Localize, { UnsafeLocalize } from '@ta/platform.i18n';

const NoLabel = () => {
  throw new Error('This reason does not have a label');
};

// Keep this in sync with IAPReason.java
// try to keep this in sorted id order
const IAPReasonConfiguration = {
  DUPLICATE_CONTENT: { id: 5, label: <UnsafeLocalize id="social_report_reason_photos2" /> }, // It&#39;s a duplicate by the same member
  WRONG_LOCATION: { id: 6, label: <Localize id="report_review_radio_8" /> }, // It's posted to the wrong business
  CLOSED: { id: 8, label: <Localize id="report_review_radio_21" /> }, // This business is closed or doesn't exist
  OTHER: { id: 9, label: <Localize id="report_review_radio_19" /> }, // 	I want to report something else
  WRITTEN_BY_COMPETITOR: { id: 19, label: <Localize id="report_review_radio_10" /> }, // It's written by an ex-employee or competitor
  OWNER_RESPONSE: { id: 21, label: <NoLabel /> },
  INAPPROPRIATE: { id: 22, label: <Localize id="report_review_radio_9" /> }, // It's inappropriate or not family friendly
  NOT_ORIGINAL_REVIEW: { id: 24, label: <Localize id="report_review_radio_16" /> }, // Another reviewer already wrote about this experience
  NOT_TRAVELERS_OWN: { id: 25, label: <Localize id="report_review_radio_13" /> }, // This reviewer did not experience my business
  PROMOTIONAL: { id: 27, label: <Localize id="report_review_radio_24" /> }, // It contains commercial or promotional content

  REVIEW_NOT_CONSISTENT_WITH_RATING: {
    id: 30,
    label: <Localize id="report_review_radio_12_ver_2" />,
  },
  // Review is not consistent with rating given
  LOW_QUALITY: { id: 31, label: <Localize id="iap_low_quality_ffffe4e2" /> }, // Low quality
  COPYRIGHT_VIOLATION: {
    id: 32,
    label: <UnsafeLocalize id="social_content_report_copyright_violation" />, // It&#39;s a violation of copyright
  },
  BIASED: { id: 37, label: <Localize id="report_review_radio_20" /> }, // It's biased or written by someone with an affiliation to the property
  MORE_THAN_1_YEAR_AGO: { id: 38, label: <Localize id="report_review_radio_11" /> }, // It describes an experience more than 1 year ago
  CONTAINS_PRIVATE_INFO: { id: 41, label: <Localize id="report_review_radio_18" /> }, // It includes private information

  PROFANE_EXPLICIT_HATE_SPEECH: {
    id: 42,
    label: <Localize id="iap_profane_explicit_or_hate_speech" />, // It's profane, sexually explicit or contains hate speech
  },
  LINK_INCORRECT_OUTDATED_BROKEN: {
    id: 43,
    label: <Localize id="iap_link_incorrect_outdated_broken" />, // Link is incorrect, outdated, broken or unrelated to travel
  },
  NOT_PERSONAL_EXPERIENCE: { id: 45, label: <Localize id="iap_not_personal_experience" /> }, // It does not describe a personal experience
  FALSE_MISLEADING_INFO: { id: 46, label: <Localize id="iap_false_misleading_info" /> }, // It contains false or misleading information
  PROMOTES_ILLEGAL_ACTIVITY: { id: 47, label: <Localize id="iap_promote_illegal_activity" /> }, // It describes or promotes participation in illegal activities
  PARTICIPATION_ILLEGAL_ACTIVITY: {
    id: 48,
    label: <Localize id="iap_participate_illegal_activity" />, // It shows participation in illegal activity
  },
  LINK_ILLEGAL_ACTIVITY: { id: 50, label: <Localize id="iap_link_illegal_activity" /> }, // It contains or links to content that promotes illegal activity

  // These are new social reporting reasons for forums. If any are added, they should be mapped to
  // the old reasons that are used in the URD (InappropriatePostReason.java). The reasons are all similar enough to be mapped.
  // Both this class and IAPReasons.jsx should ALWAYS be modified at the same time
  SELF_PROMOTION_ADVERTISEMENT_SOLICITATION: {
    id: 51,
    label: <Localize id="iap_forums_self_promotion" />,
  },
  OFF_TOPIC: { id: 52, label: <Localize id="iap_forums_off_topic" /> },
  SPAM: { id: 53, label: <Localize id="iap_forums_spam" /> },
  DUPLICATE_FORUM: { id: 54, label: <Localize id="iap_forums_duplicate" /> },
  HARASSMENT: { id: 55, label: <Localize id="iap_forums_harassment" /> },
  PROFANITY_SEX_VIOLENCE_CRIME_HATE: {
    id: 56,
    label: <Localize id="iap_forums_profanity_sex_violence_crime_hate" />,
  },
  MULTIPLE_ACCOUNTS_OR_IMPERSONATION: {
    id: 57,
    label: <Localize id="iap_forums_multiple_accounts_or_impersonation" />,
  },
  PERSONAL_INFO: { id: 58, label: <Localize id="iap_forums_personal_info" /> },
  COPYRIGHTED: { id: 59, label: <Localize id="iap_forums_copyrighted" /> },
  TRIP_COPYRIGHT: { id: 60, label: <Localize id="iap_trips_copyrighted" /> },
  TRIP_NOT_TRAVEL_RELATED: { id: 61, label: <Localize id="iap_trips_not_travel_related" /> },
  TRIP_ERROR_OR_DISAGREE: { id: 62, label: <Localize id="iap_trips_error_or_disagree" /> },

  PROFILE_PROBLEM_WITH_PHOTO: { id: 63, label: <Localize id="iap_profile_problem_with_pic" /> }, // Problem with profile photo or cover photo
  PROFILE_PROBLEM_WITH_LINK: { id: 64, label: <Localize id="iap_profile_problem_with_link" /> }, // Problem with profile page bio or website link
  PROFILE_IAP_USERNAME: { id: 65, label: <Localize id="iap_profile_username" /> }, // Username is inappropriate
  PROFILE_IAP_CONTRIBUTION: { id: 66, label: <Localize id="iap_profile_contribution" /> }, // Problem with member's contribution (review, photo, video, etc.)
  PROFILE_PROBLEM_MY_ACCOUNT: { id: 67, label: <Localize id="iap_profile_problem_my_account" /> }, // This is my account and there is an issue
  PROFILE_UNWANTED_MESSAGE: { id: 68, label: <Localize id="iap_profile_unwanted_messages" /> }, // Received unwanted messages in my inbox from this user

  // Owner flows (can't be submitted, so using negative numbers)
  REPORT_THIS_REVIEW: { id: -1, label: <Localize id="report_review_radio_1" /> }, // I want to report this review
  UPDATE_BUSINESS_DETAILS: { id: -2, label: <Localize id="report_review_radio_2" /> }, // I need to update my business or owner details
  REPORT_BUSINESS_CLOSED: { id: -3, label: <Localize id="report_review_radio_3" /> }, // I want to report my business closed
  EDIT_REMOVE_RESPONSE: { id: -4, label: <Localize id="report_review_radio_4" /> }, // I want to edit or remove my response
  REPORT_OWNER_CHANGED: { id: -5, label: <Localize id="report_review_radio_5" /> }, // I need to report a change of ownership or renovation
  REPORT_PHOTO: { id: -6, label: <Localize id="report_review_radio_6" /> }, // I want to report a photo associated with this review
  REMOVE_LISTING: { id: -7, label: <Localize id="report_review_radio_7" /> }, // I want to remove my listing
  DISAGREE_WITH_INFO: { id: -8, label: <Localize id="report_review_radio_17" /> }, // I disagree with the information in this review
};

/** The list of valid enums */
export type IAPReason = $Keys<typeof IAPReasonConfiguration>;

/** Gets the localized element and service id of an IAP reason */
const labelFor = (reason: IAPReason) => IAPReasonConfiguration[reason];

/** If the reason is restricted for the owner flow */
const isOwnerReason = (reason: IAPReason) => IAPReasonConfiguration[reason].id < 0;
export { isOwnerReason };
export default labelFor;
